import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Autocomplete,
  Avatar,
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import { useEffect } from "react";
import * as yup from "yup";
import useColors from "../../../../../../api/hooks/useColors";
import LoadingIcon from "../../../../../../components/LoadingIcon";
import AnchorPopper from "../../../../../../components/forms/AnchorPopper";
import {
  StyledInputLabel,
  StyledSelect,
  StyledTextField,
  getTextFieldInputProps,
  textFieldLabelProps,
} from "../../../../../../components/forms/styles/formFieldStyles";

export const getCustomFieldsValidationSchema = (customFields) => {
  if (!customFields?.length) return {};

  return customFields.reduce((schema, field) => {
    if (field.required) {
      const errorMessage = `${field.name} is required`;

      if (field.schema?.type === "array") {
        schema[field.key] = yup
          .array()
          .of(
            yup.object().shape({
              value: yup.string().required(),
              id: yup.string().required(),
            })
          )
          .min(1, errorMessage)
          .test(
            "is-array",
            errorMessage,
            (value) => Array.isArray(value) && value.length > 0
          );
      } else if (field.schema?.type === "option") {
        schema[field.key] = yup.object().nullable().required(errorMessage);
      } else if (field.schema?.type === "number") {
        schema[field.key] = yup.number().required(errorMessage);
      } else {
        schema[field.key] = yup.string().required(errorMessage);
      }
    }
    return schema;
  }, {});
};

const JiraCustomFields = ({ customFields, isLoading, assigneeOptions }) => {
  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext();
  const colors = useColors();

  const initializeFormCustomFieldValues = (fields) => {
    if (!fields?.length) return;

    fields.forEach((field) => {
      const fieldKey = field.key;

      // Skip if field is already initialized
      if (values[fieldKey] !== undefined) return;

      // Set appropriate initial value based on field type
      if (field.schema?.type === "array") {
        setFieldValue(fieldKey, []);
      } else if (
        field.schema?.type === "option" ||
        field.allowedValues?.length > 0
      ) {
        setFieldValue(fieldKey, null);
      } else {
        setFieldValue(fieldKey, "");
      }
    });
  };

  useEffect(() => {
    initializeFormCustomFieldValues(customFields);
  }, [customFields]);

  const FieldLabel = ({ name }) => (
    <Typography variant="caption">{name}</Typography>
  );

  return (
    <>
      {/* Generate the right MUI HTML Element based on the customfield.schema.type (it can be array, number, etc. ) */}
      {isLoading ? (
        <Box mt={2}>
          <LoadingIcon size={8} />
        </Box>
      ) : (
        <>
          {customFields &&
            customFields.map((customField) => {
              const isFieldTouched = touched?.[customField.key];
              const fieldError = errors[customField.key];
              const hasError = !!isFieldTouched && !!fieldError;
              const errorText =
                hasError && typeof fieldError === "string" ? fieldError : "";

              const arrayOptions =
                customField.schema?.items === "user"
                  ? assigneeOptions?.map(
                      ({ displayName, accountId, avatarUrls }) => ({
                        value: displayName,
                        id: accountId,
                        avatarUrls: avatarUrls,
                      })
                    ) || []
                  : customField.allowedValues?.map(({ value, id }) => ({
                      value,
                      id,
                    })) || [];

              switch (customField.schema.type) {
                case "string":
                  return (
                    <StyledTextField
                      key={customField.key}
                      margin="dense"
                      name={customField.key}
                      label={<FieldLabel name={customField.name} />}
                      fullWidth
                      required={customField.required}
                      onChange={(event) =>
                        setFieldValue(customField.key, event.target.value)
                      }
                      error={hasError}
                      helperText={errorText}
                      onBlur={handleBlur}
                      InputProps={getTextFieldInputProps()}
                      InputLabelProps={textFieldLabelProps()}
                    />
                  );
                case "number":
                  return (
                    <StyledTextField
                      key={customField.key}
                      margin="dense"
                      label={<FieldLabel name={customField.name} />}
                      fullWidth
                      required={customField.required}
                      type="number"
                      onChange={(event) =>
                        setFieldValue(
                          customField.key,
                          parseInt(event.target.value) || null
                        )
                      }
                      error={hasError}
                      helperText={errorText}
                      onBlur={handleBlur}
                      InputProps={getTextFieldInputProps()}
                      InputLabelProps={textFieldLabelProps()}
                    />
                  );
                case "option":
                  return (
                    <FormControl
                      fullWidth
                      required={customField.required}
                      margin="dense"
                      key={customField.key}
                      sx={{
                        "& .MuiFormHelperText-root": {
                          "&.Mui-error": {
                            color: colors.error_500,
                          },
                        },
                        "& .MuiInputLabel-asterisk": {
                          color: colors.error_500, // Make the asterisk red
                        },
                      }}
                    >
                      <StyledInputLabel id={`${customField.key}-label`}>
                        <FieldLabel name={customField.name} />
                      </StyledInputLabel>
                      <StyledSelect
                        labelId={`${customField.key}-label`}
                        displayEmpty
                        value={values[customField.key]?.id || ""}
                        IconComponent={ExpandMoreIcon}
                        onChange={(event) => {
                          const selectedValue = event.target.value;
                          if (selectedValue === "none") {
                            setFieldValue(customField.key, null);
                          } else {
                            const selectedOption =
                              customField.allowedValues.find(
                                (option) => option.id === selectedValue
                              );
                            setFieldValue(customField.key, selectedOption);
                          }
                        }}
                        error={hasError}
                        onBlur={handleBlur}
                      >
                        <MenuItem value="none">
                          <Typography variant="caption">
                            <em>None</em>
                          </Typography>
                        </MenuItem>
                        {customField.allowedValues.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            <Typography variant="caption">
                              {option.value}
                            </Typography>
                          </MenuItem>
                        ))}
                      </StyledSelect>
                      {hasError && (
                        <FormHelperText error>{errorText}</FormHelperText>
                      )}
                    </FormControl>
                  );
                case "array":
                  return (
                    <Autocomplete
                      options={arrayOptions}
                      getOptionLabel={(option) => option.value}
                      isOptionEqualToValue={(option, value) =>
                        option.id === (value.id || value)
                      }
                      value={values[customField.key] || []}
                      multiple
                      filterSelectedOptions
                      popupIcon={<ExpandMoreIcon />}
                      onChange={(_, newValue) =>
                        setFieldValue(customField.key, newValue)
                      }
                      PopperComponent={AnchorPopper}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => {
                          const { onDelete, ...tagProps } = getTagProps({
                            index,
                          });
                          const isUserItem =
                            customField.schema?.items === "user";

                          return (
                            <Box
                              {...tagProps}
                              key={index}
                              component="div"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                backgroundColor: colors.grey_200,
                                borderRadius: "16px",
                                padding: "3px 8px 3px 5px",
                                margin: "3px",
                              }}
                            >
                              {isUserItem && (
                                <Avatar
                                  src={option.avatarUrls?.["16x16"]}
                                  alt={option.value}
                                  sx={{ width: 16, height: 16, mr: 0.5 }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontSize: "9px" }}
                                  >
                                    {option.value?.charAt(0)}
                                  </Typography>
                                </Avatar>
                              )}
                              <Typography
                                variant="caption"
                                sx={{ lineHeight: 1 }}
                              >
                                {option.value}
                              </Typography>
                              {onDelete && (
                                <Box
                                  component="span"
                                  onClick={onDelete}
                                  sx={{
                                    cursor: "pointer",
                                    ml: 0.5,
                                    fontSize: "16px",
                                    lineHeight: 1,
                                    color: colors.grey_500,
                                    "&:hover": {
                                      color: colors.grey_700,
                                    },
                                  }}
                                >
                                  ×
                                </Box>
                              )}
                            </Box>
                          );
                        })
                      }
                      renderOption={(props, option) => {
                        return customField.schema?.items === "user" ? (
                          <Box
                            component="li"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 0.5,
                            }}
                            {...props}
                          >
                            <Avatar
                              src={option.avatarUrls?.["16x16"]}
                              alt={option.value}
                              sx={{ width: 16, height: 16 }}
                            >
                              <Typography variant="caption">
                                {option.value?.charAt(0)}
                              </Typography>
                            </Avatar>
                            <Typography variant="caption">
                              {option.value}
                            </Typography>
                          </Box>
                        ) : (
                          <Box component="li" {...props}>
                            <Typography variant="caption">
                              {option.value}
                            </Typography>
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          key={customField.key}
                          margin="dense"
                          label={<FieldLabel name={customField.name} />}
                          fullWidth
                          required={customField.required}
                          error={hasError}
                          helperText={errorText}
                          onBlur={handleBlur}
                          InputProps={getTextFieldInputProps(params.InputProps)}
                          InputLabelProps={textFieldLabelProps(
                            params.InputLabelProps
                          )}
                        />
                      )}
                    />
                  );
                default:
                  return null;
              }
            })}
        </>
      )}
    </>
  );
};
export default JiraCustomFields;
